import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { pickCards, rubAtCardRate } from "../../utils/pickCards";
import HeartButton from "../../favorites/HeartButton";
import BuyGuide from "./BuyGuide";
import CardsCheckout from "./CardsCheckout";

export const panelClass =
  "rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-[#2a2a2a]";

export function money(value, currency) {
  if (value == null) return "";
  try {
    return new Intl.NumberFormat("ru-RU", {
      style: "currency",
      currency: currency || "USD",
    }).format(value);
  } catch {
    return `${value} ${currency || ""}`.trim();
  }
}

const faceLabel = (value, currency) =>
  money(value, currency).replace(/[,.]00(?=\D*$)/, "");
const rub = (value) => `${Math.round(value).toLocaleString("ru-RU")} ₽`;
const dateRu = (value) =>
  new Date(value).toLocaleDateString("ru", { day: "numeric", month: "long" });
export const parseAmount = (text) =>
  Number(String(text).replace(",", ".").replace(/[^\d.]/g, "")) || 0;
const roundCents = (value) => Math.round(value * 100) / 100;

export function plural(n, one, few, many) {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return few;
  return many;
}

export function Cover({ src, className }) {
  return src ? (
    <img
      src={src}
      alt=""
      loading="lazy"
      className={`shrink-0 rounded bg-gray-100 object-cover ${className}`}
    />
  ) : (
    <div className={`shrink-0 rounded bg-gray-200 dark:bg-gray-700 ${className}`} />
  );
}

/** Выбранная игра с ценой из прайс-API eShop; при сбое или чужой валюте — прежняя цена каталога. */
export function withLivePrice(picked, live) {
  if (typeof live?.price !== "number" || live.currency !== picked.context.currency) {
    return { ...picked, live: { status: "failed" } };
  }
  const was = picked.game.price;
  const changed = was == null || Math.abs(live.price - was) >= 0.005;
  return {
    ...picked,
    game: {
      ...picked.game,
      price: live.price,
      original: live.original,
      percent: live.percent,
      endsAt: live.endsAt,
      rub:
        rubAtCardRate(live.price, picked.context.cards?.denominations) ??
        (changed ? null : picked.game.rub),
    },
    live: changed ? { status: "changed", was } : { status: "same" },
  };
}

export function Switch2Badge() {
  return (
    <span className="ml-1 rounded bg-red-600 px-1 align-middle text-[10px] font-semibold text-white">
      Switch 2
    </span>
  );
}

export function PriceLine({ game, currency }) {
  if (game.price == null) {
    return <span className="text-gray-400">цены пока нет</span>;
  }
  return (
    <span className="flex flex-wrap items-baseline gap-x-1.5">
      <b className="text-gray-900 dark:text-white">{money(game.price, currency)}</b>
      {game.original != null ? (
        <>
          <span className="text-xs text-gray-400 line-through">
            {money(game.original, currency)}
          </span>
          <span className="text-xs font-semibold text-red-600">−{game.percent}%</span>
        </>
      ) : null}
      {game.rub ? (
        <span className="text-xs font-medium text-indigo-700 dark:text-indigo-300">
          ≈ {rub(game.rub)}
        </span>
      ) : null}
    </span>
  );
}

function NintendoPickedGame({
  game,
  context,
  live,
  catalog,
  initialOpen = false,
  onRefreshCards,
  balance,
  onBalance,
  onSpendLeft,
  favorite,
  onClose,
}) {
  const { currency, label, cardRegion, cards } = context;
  const [buyOpen, setBuyOpen] = useState(initialOpen);
  const canCheckout = Boolean(catalog?.denominations.length) && catalog.currency === currency;
  const denominations = canCheckout ? catalog.denominations : cards?.denominations;
  const hasPrice = game.price != null;
  const balanceValue = parseAmount(balance);
  const need = hasPrice ? Math.max(0, roundCents(game.price - balanceValue)) : 0;
  const plan = useMemo(
    () => (need > 0 && denominations ? pickCards(need, denominations) : null),
    [need, denominations],
  );
  const codesLabel = plan
    ? `${plan.codes} ${plural(plan.codes, "код", "кода", "кодов")}`
    : "";
  const left = plan ? roundCents(balanceValue + plan.face - game.price) : 0;
  const title = game.seoPath ? (
    <a href={game.seoPath} className="hover:underline">
      {game.title}
    </a>
  ) : (
    game.title
  );

  return (
    <div className={`${panelClass} relative mt-3 p-4`}>
      <div className="flex gap-4">
        <Cover src={game.image} className="h-24 w-24 sm:h-28 sm:w-28" />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="text-lg font-semibold leading-snug text-gray-900 dark:text-white">
              {title}
              {game.switch2 ? <Switch2Badge /> : null}
            </div>
            {favorite ? <HeartButton item={favorite} /> : null}
          </div>
          <div className="mt-2 text-base">
            <PriceLine game={game} currency={currency} />
          </div>
          {game.endsAt ? (
            <div className="mt-1 text-xs text-red-600">скидка до {dateRu(game.endsAt)}</div>
          ) : null}
          {live?.status === "changed" ? (
            <div className="mt-1 text-xs text-amber-700 dark:text-amber-300">
              {live.was != null
                ? `Цена в eShop сегодня изменилась (была ${money(live.was, currency)}) — карты подобраны по новой.`
                : "Цена взята из eShop прямо сейчас."}
            </div>
          ) : live?.status === "same" ? (
            <div className="mt-1 text-xs text-gray-400">цена сверена с eShop</div>
          ) : null}
          {game.seoPath ? (
            <a
              href={game.seoPath}
              className="mt-1 inline-block text-xs text-indigo-600 hover:underline dark:text-indigo-400"
            >
              где сейчас дешевле →
            </a>
          ) : null}
        </div>
      </div>

      {hasPrice ? (
        <div className="mt-4 border-t border-gray-200 pt-3 text-sm dark:border-gray-700">
          {need === 0 ? (
            <div className="rounded-lg bg-green-50 px-3 py-2 text-green-800 dark:bg-green-900/30 dark:text-green-300">
              Баланса хватает — карты не нужны
            </div>
          ) : plan ? (
            <>
              <div className="text-gray-600 dark:text-gray-300">
                {balanceValue > 0 ? `Не хватает ${money(need, currency)}. ` : ""}
                Подойдут карты eShop {label}:
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                {plan.items.map((item) => (
                  <span
                    key={item.face}
                    className="rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-0.5 dark:border-indigo-700 dark:bg-indigo-900/40"
                  >
                    <b>{faceLabel(item.face, currency)}</b>
                    {item.quantity > 1 ? ` × ${item.quantity}` : ""}
                  </span>
                ))}
                <span className="font-semibold">= {rub(plan.rub)}</span>
              </div>
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {left > 0
                  ? `После покупки на балансе останется ${money(left, currency)}`
                  : "Сумма совпадает с ценой игры без остатка"}
                {left >= 1 && onSpendLeft ? (
                  <>
                    {" · "}
                    <button
                      type="button"
                      onClick={() => onSpendLeft(left)}
                      className="font-medium text-indigo-600 hover:underline dark:text-indigo-400"
                    >
                      подобрать игры на остаток
                    </button>
                  </>
                ) : null}
              </div>
            </>
          ) : !denominations?.length ? (
            <div className="rounded-lg bg-amber-50 px-3 py-2 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200">
              Каталог карт сейчас недоступен — не получилось подобрать карты. Попробуйте
              обновить страницу чуть позже.
            </div>
          ) : (
            <div className="rounded-lg bg-red-50 px-3 py-2 text-red-700 dark:bg-red-900/30 dark:text-red-300">
              Не хватает карт в наличии на {money(need, currency)}
            </div>
          )}

          <label className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
            Мой баланс eShop:
            <span className="inline-flex items-center gap-1">
              <input
                inputMode="decimal"
                value={balance}
                placeholder="0"
                onChange={(e) => onBalance(e.target.value.replace(/[^\d.,]/g, ""))}
                className="w-16 rounded border border-gray-300 px-1.5 py-0.5 text-xs text-gray-800 outline-none focus:border-indigo-500 dark:border-gray-600 dark:bg-[#222222] dark:text-gray-100"
              />
              {currency}
            </span>
            <span className="text-gray-400">необязательно, для точного расчёта</span>
          </label>

          {plan && canCheckout ? (
            <>
              <button
                type="button"
                onClick={() => setBuyOpen((current) => !current)}
                className="mt-3 flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 font-semibold text-white shadow-sm hover:bg-indigo-500"
              >
                {buyOpen ? "Скрыть оформление" : `Купить ${codesLabel} за ${rub(plan.rub)}`}
              </button>
              {buyOpen ? <CardsCheckout plan={plan} onPriceChanged={onRefreshCards} /> : null}
            </>
          ) : (
            <Link
              to={`/store/nintendo?region=${cardRegion}`}
              className="mt-3 flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 font-semibold text-white shadow-sm hover:bg-indigo-500"
            >
              Купить карты eShop {label}
            </Link>
          )}
          <div className="flex items-center justify-center gap-4">
            <BuyGuide label={label} />
            {onClose ? (
              <button
                type="button"
                onClick={onClose}
                className="mt-3 text-sm font-medium text-gray-500 hover:underline dark:text-gray-400"
              >
                Скрыть
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
          В eShop {label} цены пока нет — игра ещё не вышла или не продаётся в этом
          регионе.
        </p>
      )}
    </div>
  );
}

export default NintendoPickedGame;
